import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { PlusIcon, ArrowTopRightOnSquareIcon, PaperClipIcon, WrenchScrewdriverIcon } from '@heroicons/react/24/outline';
import { ColumnDef } from '@tanstack/react-table';
import { useLocation } from 'react-router-dom';
import PermissionsContainer from '../../components/PermissionsContainer';
import { usePermissionsMode } from '../../contexts/PermissionsModeContext';
import MultiSelect from '../../components/MultiSelect';
import { TanTable } from '../../components/TanTable';
import ProDetailSlideOver from '../../components/pro/ProDetailSlideOver';
import { ViewedProsProvider, useViewedPros } from '../../contexts/ViewedProsContext';
import { useRepairOrderViewers } from '../../hooks/useRepairOrderViewers';
import { Tooltip } from '../../components/ui/Tooltip';
import { config } from '../../config/config';
import repairOrdersService from '../../services/repairOrders.service';
import {
  useAdditionalFieldSelection,
  useAdditionalFieldColumns,
} from '../../hooks/useAdditionalFields';

/** No column here renders a registry field under a different id, so there is nothing
 *  to declare. Stable identity: a fresh object each render would re-run the picker memo. */
const EMPTY_COVERED_FIELDS = {} as const;
import RepairOrderDrawer from './components/RepairOrderDrawer';
import EquipmentRepairOrdersDrawer from './components/EquipmentRepairOrdersDrawer';
import { RepairOrderEquipmentCell } from './components/RepairOrderEquipmentCell';
import { resolveRepairUnitNumber } from './components/repairOrderUnit';
import {
  RepairOrderListItem,
  RepairOrderPing,
  RepairOrderStatusColors,
  RepairOrderStatus,
  RepairItemTypes,
} from '../../types/repairOrders';
// The feature's ONE relative-age helper — see its doc comment for why
// `requestedAt` must not be treated as UTC.
import { formatPingAge } from '../../components/RepairOrderCardList';
import { useVisibilityAwareInterval } from '../../hooks/useVisibilityAwareInterval';

/** "/YYYY" suffix for a formatted date: always when `alwaysYear`, otherwise only
 *  when the year is not the current one (the grid's default short form). */
const yearSuffixFor = (year: number, alwaysYear: boolean): string =>
  alwaysYear || year !== new Date().getFullYear() ? `/${year}` : '';

/** Format a date string (ISO datetime or MM/DD/YYYY) as MM/DD, appending /YYYY when
 *  not the current year — or always, when `alwaysYear` is set. */
export const formatShortDate = (dateStr: string | null, alwaysYear = false): string => {
  if (!dateStr) return '';
  // MM/DD/YYYY format
  const slashParts = dateStr.split('/');
  if (slashParts.length === 3) {
    const year = parseInt(slashParts[2], 10);
    return `${slashParts[0].padStart(2, '0')}/${slashParts[1].padStart(2, '0')}${yearSuffixFor(year, alwaysYear)}`;
  }
  // ISO datetime format
  const dateObj = new Date(dateStr.includes('T') ? dateStr : dateStr.replace(' ', 'T'));
  if (isNaN(dateObj.getTime())) return dateStr;
  const month = String(dateObj.getMonth() + 1).padStart(2, '0');
  const day = String(dateObj.getDate()).padStart(2, '0');
  return `${month}/${day}${yearSuffixFor(dateObj.getFullYear(), alwaysYear)}`;
};

/** Format a date string (MM/DD/YYYY [HH:MM [- HH:MM]] or ISO datetime) as MM/DD h:mm AM/PM,
 *  appending /YYYY when not the current year. Falls back to date-only when no time is present.
 *
 *  `alwaysYear` forces MM/DD/YYYY h:mm AM/PM. The Excel export runs this cell
 *  renderer, and the short year-less form is text Excel/Power Query cannot parse
 *  as a datetime — so datetime columns users query in Excel (Vendor ETA, Last
 *  Note Date) always carry the year. */
export const formatShortDateTime = (dateStr: string | null, alwaysYear = false): string => {
  if (!dateStr) return '';
  // Backend sends "MM/DD/YYYY HH:MM" (24h); parse the slash form directly.
  const m = dateStr.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:[ T](\d{1,2}):(\d{2})(?: - (\d{1,2}):(\d{2}))?)?/);
  if (m) {
    const [, mm, dd, yyyy, hh, min, endHour, endMinute] = m;
    if (hh === undefined) return formatShortDate(dateStr, alwaysYear);
    const dateObj = new Date(+yyyy, +mm - 1, +dd, +hh, +min);
    const time = dateObj.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    const endTime = endHour === undefined ? '' : ` - ${new Date(+yyyy, +mm - 1, +dd, +endHour, +endMinute)
      .toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`;
    return `${mm.padStart(2, '0')}/${dd.padStart(2, '0')}${yearSuffixFor(+yyyy, alwaysYear)} ${time}${endTime}`;
  }
  // ISO datetime fallback.
  if (!/[T ]\d{1,2}:\d{2}/.test(dateStr)) return formatShortDate(dateStr, alwaysYear);
  const dateObj = new Date(dateStr.includes('T') ? dateStr : dateStr.replace(' ', 'T'));
  if (isNaN(dateObj.getTime())) return formatShortDate(dateStr, alwaysYear);
  const month = String(dateObj.getMonth() + 1).padStart(2, '0');
  const day = String(dateObj.getDate()).padStart(2, '0');
  const time = dateObj.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  return `${month}/${day}${yearSuffixFor(dateObj.getFullYear(), alwaysYear)} ${time}`;
};

/** Parse a "MM/DD/YYYY" or "MM/DD/YYYY HH:MM" string to a timestamp for sorting. Returns 0 for null/invalid. */
const parseMDY = (v: string | null): number => {
  if (!v) return 0;
  const m = v.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:[ T](\d{1,2}):(\d{2}))?/);
  if (!m) return 0;
  const [, mm, dd, yyyy, hh, min] = m;
  return new Date(+yyyy, +mm - 1, +dd, hh ? +hh : 0, min ? +min : 0).getTime();
};

const formatCurrency = (value: number | null): string => {
  if (value == null) return '-';
  return value.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
};

/**
 * The equipment number a row shows. Thin row-shaped wrapper over
 * `resolveRepairUnitNumber`, which owns the chassisid-vs-container rule for
 * the whole feature — see that function for why repairpart is matched
 * case-insensitively and why `container` falls back to `chassisid`.
 *
 * Keeping this on the shared helper is what makes the Equipment # column and
 * the ROs badge beside it agree: the badge's unit comes from the backend's
 * mirror of the same rule.
 */
export const equipmentNumberFor = (
  row: Pick<RepairOrderListItem, 'repairpart' | 'chassisid' | 'container'>,
): string => resolveRepairUnitNumber(row.repairpart, row.chassisid, row.container);

/** Short chip label per request type — the grid cell has a column's width, not
 *  a card's, so "ETA requested" is trimmed to what the header already frames. */
const PING_CHIP_LABELS: Record<string, string> = {
  eta: 'ETA',
  completion: 'Completion',
};

/**
 * The Requests column's cell: one amber chip per outstanding maintenance
 * request, requester + age in the tooltip. Blank when the order has none —
 * this column is about what is waiting on maintenance, and a "-" in thousands
 * of rows is noise, not information.
 *
 * Age comes from `formatPingAge`, the single relative-age helper this feature
 * has. `requestedAt` is naive LOCAL time (no 'Z'); that helper already parses
 * it correctly, which is exactly why it is reused rather than reimplemented.
 */
export const OpenRequestsCell: React.FC<{ pings: RepairOrderPing[] }> = ({ pings }) => {
  if (!pings || pings.length === 0) return null;
  return (
    <div className="flex flex-wrap items-center gap-1">
      {pings.map((p) => (
        <span
          key={p.recno}
          title={[
            PING_CHIP_LABELS[p.requestType] || p.requestType,
            p.requestedBy,
            formatPingAge(p.requestedAt),
          ].filter(Boolean).join(' · ')}
          className="px-2 py-1 text-xs font-medium rounded-full bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400"
        >
          {PING_CHIP_LABELS[p.requestType] || p.requestType}
        </span>
      ))}
    </div>
  );
};

/** "Has open request" quick-filter predicate: rows maintenance owes an answer
 *  on. Defensive about a missing array — an older cached payload has no
 *  `open_pings` at all. */
export const hasOpenRequest = (row: RepairOrderListItem): boolean =>
  (row.open_pings?.length ?? 0) > 0;

/**
 * Columns the grid hides until a user opts in via the column picker. Exported
 * so the default stays assertable: adding a column visible-by-default would
 * shift every saved layout on the page.
 */
export const REPAIR_ORDERS_DEFAULT_COLUMN_VISIBILITY: Record<string, boolean> = {
  v2repairorderid: false,
  repairpart: false,
  reportedbytype: false,
  reportedbyname: false,
  locationdesc: false,
  estimatedcost: false,
  invoicenumber: false,
  invoicetotal: false,
  vendoretadatetime: false,
  // Hidden by default like the sibling billing columns; the export
  // emits them regardless (exportColumnOrder is independent of
  // on-screen visibility). Flip to true if these should show up
  // for everyone without opting in via the column picker.
  billedtotype: false,
  billedtoname: false,
  // Deliberate: maintenance opts in (or uses the quick filter). Nobody's saved
  // layout should move because this feature shipped.
  open_pings: false,
};

const STATUS_OPTIONS = Object.values(RepairOrderStatus)
  .filter((status) => status !== RepairOrderStatus.Draft)
  .map((status) => ({
    value: status,
    label: status,
  }));

const REPAIR_ITEM_OPTIONS = RepairItemTypes.map((type) => ({
  value: type,
  label: type,
}));

// Default status filter: the actionable buckets. Pending replaced Deferred here
// (2026-09-02) — Deferred ROs are parked and shouldn't crowd the default view.
export const DEFAULT_STATUS_FILTER = [RepairOrderStatus.Open, RepairOrderStatus.Dispatched, RepairOrderStatus.Pending];

// Auto-refresh interval (60 seconds)
const REFRESH_INTERVAL = 60000;

const RepairOrdersContent: React.FC = () => {
  const { isViewed, markViewed } = useViewedPros();
  const [orders, setOrders] = useState<RepairOrderListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string[]>(DEFAULT_STATUS_FILTER);
  const [repairItemFilter, setRepairItemFilter] = useState<string[]>([]);
  const [vendorAssignedFilter, setVendorAssignedFilter] = useState<string>('all');
  const [regionFilter, setRegionFilter] = useState<string[]>([]);
  // Client-side, unlike its neighbours: open pings are enrichment attached to
  // the rows the backend already returned, not a cache column the list endpoint
  // can filter on.
  const [openRequestFilter, setOpenRequestFilter] = useState<string>('all');
  const [regionOptions, setRegionOptions] = useState<{ value: string; label: string }[]>([]);

  // Arriving from the sidebar's collapsed "N more requests waiting" toast, which
  // navigates to `?requests=open`. Driven off useLocation rather than read once
  // at mount so it also lands when the user is ALREADY on this page — a route
  // change to the same path does not remount the component.
  const { search: locationSearch } = useLocation();
  useEffect(() => {
    let requested: string | null;
    try {
      requested = new URLSearchParams(locationSearch).get('requests');
    } catch {
      requested = null;
    }
    if (requested === 'open') setOpenRequestFilter('open');
  }, [locationSearch]);

  // Repair order drawer state
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedOrderRecno, setSelectedOrderRecno] = useState<number | null>(null);
  const { repairOrderViewers } = useRepairOrderViewers({ activeRepairOrderId: selectedOrderRecno });

  // Order details drawer state
  const [selectedPro, setSelectedPro] = useState<number | null>(null);
  const [isOrderDrawerOpen, setIsOrderDrawerOpen] = useState(false);

  // Equipment ROs sidebar state — the unit whose repair orders to list
  const [equipmentRosUnit, setEquipmentRosUnit] = useState<string | null>(null);

  const { setPageComponents } = usePermissionsMode();

  // Register page components for permissions mode
  const pageComponentsList = useMemo(() => [
    { id: 'repair-orders-header', name: 'Page Header' },
    { id: 'repair-orders-table', name: 'Repair Orders Table' },
  ], []);

  useEffect(() => {
    setPageComponents(pageComponentsList);
    return () => setPageComponents([]);
  }, [pageComponentsList, setPageComponents]);

  // Load region options on mount
  useEffect(() => {
    repairOrdersService.getRegions().then((res) => {
      setRegionOptions((res.data || []).map((r) => ({ value: r, label: r })));
    }).catch(() => {});
  }, []);

  // Any orders-scope registry field can be added as a column here: the backend
  // attaches it to the rows this endpoint already returned, keyed on pronumber.
  // Row-expanding fields (invoice line items) stay off — a row is one repair
  // order, and fanning it per invoice line would double-count the grid's counts.
  const fieldSelection = useAdditionalFieldSelection('repair-orders');
  const additionalFieldsKey = fieldSelection.additionalFields.join(',');
  const fieldsReady = fieldSelection.ready;

  const abortControllerRef = useRef<AbortController | null>(null);

  const fetchData = useCallback(async (showLoading = true, signal?: AbortSignal) => {
    if (showLoading) setLoading(true);
    setError(null);
    try {
      const params: {
        status?: string; repairpart?: string; vendor_assigned?: boolean;
        region?: string; additional_fields?: string;
      } = {};
      if (statusFilter.length > 0) params.status = statusFilter.join(',');
      if (repairItemFilter.length > 0) params.repairpart = repairItemFilter.join(',');
      if (vendorAssignedFilter === 'yes') params.vendor_assigned = true;
      if (vendorAssignedFilter === 'no') params.vendor_assigned = false;
      if (regionFilter.length > 0) params.region = regionFilter.join(',');
      if (additionalFieldsKey) params.additional_fields = additionalFieldsKey;

      const response = await repairOrdersService.list(params, signal);
      setOrders(response.data || []);
    } catch (err: any) {
      if (err?.code === 'ERR_CANCELED') return;
      setError(err.response?.data?.detail || 'Failed to fetch repair orders');
      setOrders([]);
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, [statusFilter, repairItemFilter, vendorAssignedFilter, regionFilter, additionalFieldsKey]);

  // Only auto-refresh when filters are applied. The unfiltered query returns all
  // 78k+ rows and is slow — repeatedly polling it ties up the backend without
  // giving the user new useful data, so we poll only the narrowed (filtered) view.
  const hasAnyFilter =
    statusFilter.length > 0 ||
    repairItemFilter.length > 0 ||
    vendorAssignedFilter !== 'all' ||
    regionFilter.length > 0;

  // Initial fetch — abort in-flight requests when filters change
  useEffect(() => {
    if (!fieldsReady) return;   // the saved column selection is part of the request
    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    fetchData(true, controller.signal);

    return () => controller.abort();
  }, [fetchData, hasAnyFilter, fieldsReady]);

  // Background auto-refresh (only active when filters are applied)
  useVisibilityAwareInterval(
    useCallback(() => {
      abortControllerRef.current?.abort();
      const refreshController = new AbortController();
      abortControllerRef.current = refreshController;
      fetchData(false, refreshController.signal);
    }, [fetchData]),
    hasAnyFilter ? REFRESH_INTERVAL : null
  );

  const handleNewOrder = useCallback(() => {
    setSelectedOrderRecno(null);
    setDrawerOpen(true);
  }, []);

  const handleRowClick = useCallback((row: RepairOrderListItem) => {
    setSelectedOrderRecno(row.recno);
    setDrawerOpen(true);
  }, []);

  const handleDrawerClose = useCallback(() => {
    setDrawerOpen(false);
    setSelectedOrderRecno(null);
  }, []);

  const handleOrderSaved = useCallback(() => {
    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;
    fetchData(true, controller.signal);
  }, [fetchData]);

  // Order details drawer handlers
  const openOrderDrawer = useCallback((pronumber: number) => {
    setSelectedPro(pronumber);
    setIsOrderDrawerOpen(true);
  }, []);

  const closeOrderDrawer = useCallback(() => {
    setIsOrderDrawerOpen(false);
    setSelectedPro(null);
  }, []);

  // Column definitions. Order here is the default visual order; columns hidden
  // by default (see defaultColumnVisibility on TanTable below) are placed at
  // the end so the visible defaults read left-to-right per the screenshot.
  const baseColumns = useMemo<ColumnDef<RepairOrderListItem>[]>(() => [
    {
      accessorKey: 'orderstatus',
      header: 'Status',
      enableSorting: true,
      filterFn: 'arrIncludes' as any,
      cell: ({ row }) => {
        const status = row.original.orderstatus;
        const normalized = status ? status.charAt(0).toUpperCase() + status.slice(1).toLowerCase() : '';
        const colorClass = normalized ? RepairOrderStatusColors[normalized] || RepairOrderStatusColors.Draft : RepairOrderStatusColors.Draft;
        return (
          <span className={`px-2 py-1 text-xs font-medium rounded-full ${colorClass}`}>
            {status || 'Unknown'}
          </span>
        );
      },
    },
    {
      accessorKey: 'recno',
      header: 'RO #',
      enableSorting: true,
      filterFn: 'arrIncludes' as any,
      cell: ({ row }) => (
        <span className="font-medium text-heading">{row.original.recno}</span>
      ),
    },
    {
      accessorKey: 'reschedule_count',
      header: 'SF',
      enableSorting: true,
      filterFn: 'arrIncludes' as any,
      cell: ({ row }) => {
        const count = row.original.reschedule_count;
        const history = row.original.reschedule_history || [];
        if (count < 1) return null;

        const tooltipContent = history.length > 0 ? (
          <div className="text-xs space-y-2 max-w-xs">
            <div className="font-semibold border-b border-gray-300 pb-1 mb-1 text-body">
              Service Failure History ({count})
            </div>
            {history.map((entry, idx) => (
              <div key={idx} className="space-y-0.5">
                <div className="text-gray-500">{entry.old_date} → {entry.new_date}</div>
                <div className="text-body font-medium">{entry.reason || 'No reason provided'}</div>
                <div className="text-gray-400 text-[0.625rem]">{entry.created}</div>
              </div>
            ))}
          </div>
        ) : null;

        return (
          <Tooltip content={tooltipContent} placement="right">
            <span
              className={`inline-flex items-center justify-center w-5 h-5 text-xs font-bold text-white rounded cursor-help ${
                count === 1 ? 'bg-orange-500' : 'bg-red-500'
              }`}
            >
              {count}
            </span>
          </Tooltip>
        );
      },
    },
    {
      accessorKey: 'attachment_count',
      header: 'Attachments',
      enableSorting: true,
      filterFn: 'arrIncludes' as any,
      cell: ({ row }) => {
        const count = row.original.attachment_count;
        const details = row.original.attachment_details || [];
        if (count < 1) return null;

        const tooltipContent = details.length > 0 ? (
          <div className="text-xs space-y-2 max-w-xs">
            <div className="font-semibold border-b border-gray-300 pb-1 mb-1 text-body">
              Attachments ({count})
            </div>
            {details.map((doc, idx) => (
              <div key={idx} className="space-y-0.5">
                <div className="text-body font-medium">{doc.documenttype || 'Document'}</div>
                <div className="text-gray-500">{doc.uploadedby || 'Unknown'}</div>
                <div className="text-gray-400 text-[0.625rem]">{doc.uploaded}</div>
              </div>
            ))}
          </div>
        ) : null;

        return (
          <Tooltip content={tooltipContent} placement="right">
            <span className="inline-flex items-center gap-1 px-1.5 h-5 text-xs font-medium text-gray-700 bg-gray-100 dark:bg-gray-700 dark:text-gray-200 rounded cursor-help">
              <PaperClipIcon className="h-3 w-3" />
              {count}
            </span>
          </Tooltip>
        );
      },
    },
    {
      accessorKey: 'pronumber',
      header: 'Pro #',
      enableSorting: true,
      filterFn: 'arrIncludes' as any,
      cell: ({ row }) => {
        const pronumber = row.original.pronumber;
        if (!pronumber) return <span className="text-muted">-</span>;
        const viewed = isViewed(pronumber);
        return (
          <div
            className={`flex items-center gap-1 px-1 rounded transition-colors duration-150 ${
              viewed ? 'bg-green-200 dark:bg-green-600/50' : ''
            }`}
            title={viewed ? 'Viewed' : undefined}
          >
            <button
              onClick={(e) => {
                e.stopPropagation();
                markViewed(pronumber);
                openOrderDrawer(pronumber);
              }}
              className="text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 hover:underline"
            >
              {pronumber}
            </button>
            <a
              href={`${config.LEGACY_CMS_URL}/index_cms.pl?action=savecustomsearch&contlist=${pronumber}&searchtype=pronumber`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => {
                e.stopPropagation();
                markViewed(pronumber);
              }}
              className="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
              title="Open in CMSV1"
            >
              <ArrowTopRightOnSquareIcon className="h-4 w-4" />
            </a>
          </div>
        );
      },
    },
    {
      accessorKey: 'createdatetime',
      header: 'Created',
      // The REPAIR ORDER's created date, not the order's `createdatetime` its id
      // collides with — so it keeps its own label.
      meta: { registryTitle: false },
      enableSorting: true,
      cell: ({ row }) => formatShortDate(row.original.createdatetime),
    },
    {
      accessorKey: 'perdiem_date',
      header: 'Per Diem',
      enableSorting: true,
      filterFn: 'arrIncludes' as any,
      sortingFn: (a, b) => parseMDY(a.original.perdiem_date) - parseMDY(b.original.perdiem_date),
      cell: ({ row }) => formatShortDate(row.original.perdiem_date),
    },
    {
      accessorKey: 'cutoff_date',
      header: 'Cutoff',
      enableSorting: true,
      filterFn: 'arrIncludes' as any,
      sortingFn: (a, b) => parseMDY(a.original.cutoff_date) - parseMDY(b.original.cutoff_date),
      cell: ({ row }) => formatShortDateTime(row.original.cutoff_date),
    },
    {
      accessorKey: 'appointment_date',
      header: 'Appointment',
      enableSorting: true,
      filterFn: 'arrIncludes' as any,
      sortingFn: (a, b) => parseMDY(a.original.appointment_date) - parseMDY(b.original.appointment_date),
      cell: ({ row }) => formatShortDateTime(row.original.appointment_date),
    },
    {
      id: 'equipment_number',
      header: 'Equipment #',
      accessorFn: (row) => equipmentNumberFor(row),
      enableSorting: true,
      filterFn: 'arrIncludes' as any,
      cell: ({ row }) => {
        const equipNum = equipmentNumberFor(row.original);
        if (!equipNum) return '';
        const partLc = (row.original.repairpart || '').toLowerCase();
        const isGwiEquipment =
          (partLc === 'chassis' && row.original.chassispool === 'GWI') ||
          (partLc !== 'chassis' && partLc !== 'container' && partLc !== 'truck');
        return (
          <RepairOrderEquipmentCell
            unit={equipNum}
            isGwiEquipment={isGwiEquipment}
            hasCustomerLocations={row.original.equipment_has_customer_locations}
          />
        );
      },
    },
    {
      accessorKey: 'equipment_ro_count',
      header: 'ROs',
      enableSorting: true,
      filterFn: 'arrIncludes' as any,
      cell: ({ row }) => {
        // Lifetime RO count for the row's fleet unit, which the server derives
        // (chassisid, or container for Truck/Van/Mule/... orders the drawer
        // stored there); container-only rows have no unit and no count. Opens
        // the equipment ROs sidebar — same cards as the Pro Details ROs tab.
        const count = row.original.equipment_ro_count;
        const unit = row.original.equipment_unit;
        if (!count || !unit) return null;
        return (
          <button
            onClick={(e) => {
              e.stopPropagation();
              setEquipmentRosUnit(unit);
            }}
            title={`View all ${count} repair orders for ${unit}`}
            className="inline-flex items-center gap-1 px-1.5 h-5 text-xs font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 dark:bg-blue-900/30 dark:text-blue-300 dark:hover:bg-blue-900/50 rounded"
          >
            <WrenchScrewdriverIcon className="h-3 w-3" />
            {count}
          </button>
        );
      },
    },
    {
      accessorKey: 'waiting',
      header: 'Waiting',
      enableSorting: true,
      filterFn: 'arrIncludes' as any,
      cell: ({ row }) => {
        const waiting = row.original.waiting;
        if (waiting !== 'Y') return null;
        return (
          <span className="px-2 py-1 text-xs font-medium rounded-full bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400">
            Yes
          </span>
        );
      },
    },
    {
      accessorKey: 'locationtype',
      header: 'Location Type',
      enableSorting: true,
      filterFn: 'arrIncludes' as any,
    },
    {
      accessorKey: 'primary_issue',
      header: 'Issue',
      enableSorting: true,
      filterFn: 'arrIncludes' as any,
    },
    {
      accessorKey: 'vendor_name',
      header: 'Vendor',
      enableSorting: true,
      filterFn: 'arrIncludes' as any,
      cell: ({ row }) => {
        const { vendor_name, vendor_assigned, orderstatus } = row.original;
        if (orderstatus === 'Pending') {
          return (
            <span className="px-2 py-1 text-xs font-medium rounded-full bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400">
              Pending
            </span>
          );
        }
        if (orderstatus === 'Deferred') {
          return (
            <span className="px-2 py-1 text-xs font-medium rounded-full bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400">
              Deferred
            </span>
          );
        }
        if (vendor_assigned && vendor_name) {
          return (
            <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">
              {vendor_name}
            </span>
          );
        }
        if (orderstatus === 'Completed' || orderstatus === 'Cancelled' || orderstatus === 'Closed') {
          return <span className="text-gray-400 dark:text-gray-500">-</span>;
        }
        return (
          <span className="px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400">
            Unassigned
          </span>
        );
      },
    },
    {
      accessorKey: 'estimateddispatchdatetime',
      header: 'Est. Dispatch',
      enableSorting: true,
      filterFn: 'arrIncludes' as any,
      sortingFn: (a, b) => parseMDY(a.original.estimateddispatchdatetime) - parseMDY(b.original.estimateddispatchdatetime),
      cell: ({ row }) => formatShortDate(row.original.estimateddispatchdatetime),
    },
    {
      accessorKey: 'repairdispatchdatetime',
      header: 'Dispatched',
      enableSorting: true,
      filterFn: 'arrIncludes' as any,
      sortingFn: (a, b) => parseMDY(a.original.repairdispatchdatetime) - parseMDY(b.original.repairdispatchdatetime),
      cell: ({ row }) => formatShortDate(row.original.repairdispatchdatetime),
    },
    {
      accessorKey: 'repaircompletedatetime',
      header: 'Completed',
      enableSorting: true,
      filterFn: 'arrIncludes' as any,
      sortingFn: (a, b) => parseMDY(a.original.repaircompletedatetime) - parseMDY(b.original.repaircompletedatetime),
      cell: ({ row }) => formatShortDate(row.original.repaircompletedatetime),
    },
    {
      accessorKey: 'completedby',
      header: 'Completed By',
      enableSorting: true,
      filterFn: 'arrIncludes' as any,
      cell: ({ row }) => row.original.completedby || <span className="text-gray-400 dark:text-gray-500">-</span>,
    },
    {
      accessorKey: 'last_note',
      header: 'Last Note',
      enableSorting: true,
      filterFn: 'arrIncludes' as any,
      cell: ({ row }) => {
        const note = row.original.last_note;
        if (!note) return <span className="text-gray-400 dark:text-gray-500">-</span>;
        return (
          <span className="block max-w-xs truncate" title={note}>
            {note}
          </span>
        );
      },
    },
    {
      accessorKey: 'last_note_date',
      header: 'Last Note Date',
      enableSorting: true,
      filterFn: 'arrIncludes' as any,
      sortingFn: (a, b) => parseMDY(a.original.last_note_date) - parseMDY(b.original.last_note_date),
      // Always show the year — this value is exported as-is and queried in Excel.
      cell: ({ row }) => formatShortDateTime(row.original.last_note_date, true),
    },
    {
      accessorKey: 'last_note_by',
      header: 'Last Note By',
      enableSorting: true,
      filterFn: 'arrIncludes' as any,
      cell: ({ row }) => row.original.last_note_by || <span className="text-gray-400 dark:text-gray-500">-</span>,
    },
    // Hidden by default — see defaultColumnVisibility below.
    {
      id: 'v2repairorderid',
      header: 'v2 RO #',
      accessorFn: (row) => row.v2repairorderid ? String(row.v2repairorderid) : '',
      enableSorting: true,
      filterFn: 'arrIncludes' as any,
      cell: ({ row }) => {
        const v2id = row.original.v2repairorderid;
        if (!v2id) return <span className="text-gray-400 dark:text-gray-500">-</span>;
        return <span className="font-medium text-heading">{v2id}</span>;
      },
    },
    {
      accessorKey: 'repairpart',
      header: 'Repair Item',
      enableSorting: true,
      filterFn: 'arrIncludes' as any,
    },
    {
      accessorKey: 'category_name',
      header: 'Repair Category',
      enableSorting: true,
      filterFn: 'arrIncludes' as any,
      cell: ({ row }) => row.original.category_name || <span className="text-gray-400 dark:text-gray-500">-</span>,
    },
    {
      accessorKey: 'reportedbytype',
      header: 'Source',
      enableSorting: true,
      filterFn: 'arrIncludes' as any,
      cell: ({ row }) => {
        const source = row.original.reportedbytype;
        const isDriver = source === 'Driver';
        return (
          <span className={`px-2 py-1 text-xs font-medium rounded-full ${
            isDriver
              ? 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-400'
              : 'bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-400'
          }`}>
            {source || 'Unknown'}
          </span>
        );
      },
    },
    {
      accessorKey: 'reportedbyname',
      header: 'Reported By',
      enableSorting: true,
      filterFn: 'arrIncludes' as any,
    },
    {
      accessorKey: 'locationdesc',
      header: 'Location',
      enableSorting: true,
      filterFn: 'arrIncludes' as any,
    },
    {
      accessorKey: 'estimatedcost',
      header: 'Est. Cost',
      enableSorting: true,
      filterFn: 'arrIncludes' as any,
      cell: ({ row }) => formatCurrency(row.original.estimatedcost),
    },
    {
      accessorKey: 'invoicenumber',
      header: 'Invoice #',
      enableSorting: true,
      filterFn: 'arrIncludes' as any,
      cell: ({ row }) => row.original.invoicenumber || <span className="text-gray-400 dark:text-gray-500">-</span>,
    },
    {
      accessorKey: 'invoicetotal',
      header: 'Invoice Total',
      enableSorting: true,
      filterFn: 'arrIncludes' as any,
      cell: ({ row }) => formatCurrency(row.original.invoicetotal),
    },
    {
      accessorKey: 'vendoretadatetime',
      header: 'Vendor ETA',
      enableSorting: true,
      filterFn: 'arrIncludes' as any,
      sortingFn: (a, b) => parseMDY(a.original.vendoretadatetime) - parseMDY(b.original.vendoretadatetime),
      // Always show the year — this value is exported as-is and queried in Excel.
      cell: ({ row }) => formatShortDateTime(row.original.vendoretadatetime, true),
    },
    {
      accessorKey: 'billedtotype',
      header: 'Billed To Type',
      enableSorting: true,
      filterFn: 'arrIncludes' as any,
      cell: ({ row }) => row.original.billedtotype || <span className="text-gray-400 dark:text-gray-500">-</span>,
    },
    {
      accessorKey: 'billedtoname',
      header: 'Billed To',
      enableSorting: true,
      filterFn: 'arrIncludes' as any,
      cell: ({ row }) => row.original.billedtoname || <span className="text-gray-400 dark:text-gray-500">-</span>,
    },
    {
      accessorKey: 'open_pings',
      header: 'Requests',
      enableSorting: true,
      filterFn: 'arrIncludes' as any,
      sortingFn: (a, b) =>
        (a.original.open_pings?.length ?? 0) - (b.original.open_pings?.length ?? 0),
      cell: ({ row }) => <OpenRequestsCell pings={row.original.open_pings} />,
    },
  ], [openOrderDrawer, isViewed, markViewed]);

  const visibleOrders = useMemo(
    () => (openRequestFilter === 'open' ? orders.filter(hasOpenRequest) : orders),
    [orders, openRequestFilter],
  );

  // This grid has no renamed columns standing in for registry fields, so the
  // picker's id/mapped-name collision rule is the whole de-duplication — which is
  // what keeps the registry's `revenueregion` from colliding with the repair
  // region already on screen as `region`.
  const { columns, availableFields } = useAdditionalFieldColumns<RepairOrderListItem>(
    fieldSelection,
    { baseColumns, coveredFields: EMPTY_COVERED_FIELDS },
  );

  return (
    <div className="p-2 sm:p-4 pb-16 sm:pb-24 space-y-2 sm:space-y-4 flex flex-col h-[calc(100dvh-8rem)]">
      {/* Filters */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-1.5 sm:gap-4">
        <PermissionsContainer componentId="repair-orders-header" componentName="Page Header">
          <button
            onClick={handleNewOrder}
            className="w-full sm:w-auto flex items-center justify-center gap-2 px-2 py-[5px] sm:px-4 sm:py-3 text-xs sm:text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg"
          >
            <PlusIcon className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
            <span className="landscape:hidden">New Repair Order</span>
          </button>
        </PermissionsContainer>
        <MultiSelect
          options={STATUS_OPTIONS}
          value={statusFilter}
          onChange={setStatusFilter}
          placeholder="All Statuses"
          showClearOption
          clearOptionLabel="All Statuses"
          preserveOrder
          size="sm"
          className="w-full sm:w-56"
        />
        <MultiSelect
          options={REPAIR_ITEM_OPTIONS}
          value={repairItemFilter}
          onChange={setRepairItemFilter}
          placeholder="All Repair Items"
          showClearOption
          clearOptionLabel="All Repair Items"
          preserveOrder
          size="sm"
          className="w-full sm:w-56"
        />
        <select
          value={vendorAssignedFilter}
          onChange={(e) => setVendorAssignedFilter(e.target.value)}
          className="w-full sm:w-auto py-1 ps-2 pe-6 text-xs border border-gray-400 rounded-lg bg-white dark:bg-gray-900 dark:border-gray-500 text-heading focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-gray-600 cursor-pointer"
        >
          <option value="all">Vendor: All</option>
          <option value="yes">Vendor: Assigned</option>
          <option value="no">Vendor: Not Assigned</option>
        </select>
        <select
          value={openRequestFilter}
          onChange={(e) => setOpenRequestFilter(e.target.value)}
          aria-label="Open maintenance requests"
          className="w-full sm:w-auto py-1 ps-2 pe-6 text-xs border border-gray-400 rounded-lg bg-white dark:bg-gray-900 dark:border-gray-500 text-heading focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-gray-600 cursor-pointer"
        >
          <option value="all">Requests: All</option>
          <option value="open">Has open request</option>
        </select>
        <MultiSelect
          options={regionOptions}
          value={regionFilter}
          onChange={setRegionFilter}
          placeholder="All Regions"
          showClearOption
          clearOptionLabel="All Regions"
          preserveOrder
          size="sm"
          className="w-full sm:w-56"
        />
      </div>

      {/* Error State */}
      {error && (
        <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md text-red-700 dark:text-red-400">
          {error}
        </div>
      )}

      {/* Table */}
      <PermissionsContainer componentId="repair-orders-table" componentName="Repair Orders Table" className="flex-1 min-h-0">
        <div style={{ minHeight: '50dvh' }}>
          <TanTable
            data={visibleOrders}
            columns={columns}
            availableFields={availableFields}
            additionalFields={fieldSelection.additionalFields}
            onAdditionalFieldsChange={fieldSelection.onAdditionalFieldsChange}
            loading={loading}
            fillContainer
            cardTitle="Repair Orders"
            settingsKey="repair-orders"
            searchPlaceholder="Search by RO#, v2 RO#, equipment, name, or pro#..."
            enableColumnFilters={true}
            enableExcelExport={true}
            // Strict export: this table emits a fixed, business-specified
            // column order for Excel, independent of the user's on-screen
            // layout. Dispatched/Completed use the *_full datetime fields
            // (the on-screen columns stay date-only); "Reported By" stands in
            // for the requested "Created By".
            exportColumnOrder={[
              { key: 'orderstatus', header: 'Status' },
              { key: 'recno', header: 'RO #' },
              { key: 'pronumber', header: 'Pro #' },
              { key: 'createdatetime', header: 'Created' },
              { key: 'reportedbyname', header: 'Reported By' },
              { key: 'estimateddispatchdatetime', header: 'Est. Dispatch' },
              { key: 'repairdispatchdatetime_full', header: 'Dispatched' },
              { key: 'dispatchedby', header: 'Dispatched By' },
              { key: 'vendoretadatetime', header: 'Vendor ETA' },
              { key: 'repaircompletedatetime_full', header: 'Completed' },
              { key: 'completedby', header: 'Completed By' },
              { key: 'perdiem_date', header: 'Per Diem' },
              { key: 'cutoff_date', header: 'Cutoff' },
              { key: 'appointment_date', header: 'Appointment' },
              { key: 'reschedule_count', header: 'RS' },
              { key: 'repairpart', header: 'Repair Item' },
              { key: 'equipment_number', header: 'Equipment #' },
              { key: 'equipment_ro_count', header: 'ROs' },
              { key: 'chassispool', header: 'Chassis Pool' },
              { key: 'waiting', header: 'Waiting' },
              { key: 'withequipment', header: 'With Equipment' },
              { key: 'vendor_name', header: 'Vendor' },
              { key: 'repairtype', header: 'Repair Type' },
              { key: 'category_name', header: 'Category' },
              { key: 'primary_issue', header: 'Issue' },
              { key: 'repairqty', header: 'Qty' },
              { key: 'tire_size', header: 'Tire Size' },
              { key: 'repaircomments', header: 'Repair Comments' },
              { key: 'drivercomments', header: 'Driver Comments' },
              { key: 'locationtype', header: 'Location Type' },
              { key: 'customerlocationname', header: 'Customer Location' },
              { key: 'location_name', header: 'CMS Location' },
              { key: 'breakdownaddress', header: 'Breakdown Address' },
              { key: 'region', header: 'Region' },
              { key: 'repaireta', header: 'Repair ETA' },
              { key: 'estimatedcost', header: 'Est. Cost' },
              { key: 'invoicenumber', header: 'Invoice #' },
              { key: 'invoicetotal', header: 'Invoice Total' },
              { key: 'billedtotype', header: 'Billed To Type' },
              { key: 'billedtoname', header: 'Billed To' },
              { key: 'last_note', header: 'Last Note' },
              { key: 'last_note_date', header: 'Last Note Date' },
              { key: 'last_note_by', header: 'Last Note By' },
            ]}
            defaultSorting={[{ id: 'createdatetime', desc: true }]}
            defaultColumnVisibility={REPAIR_ORDERS_DEFAULT_COLUMN_VISIBILITY}
            defaultPageSize={100}
            onRowClick={handleRowClick}
            dateColumns={['createdatetime', 'lastchangedatetime', 'perdiem_date', 'cutoff_date', 'appointment_date', 'estimateddispatchdatetime', 'repairdispatchdatetime', 'repaircompletedatetime', 'vendoretadatetime', 'last_note_date']}
          />
        </div>
      </PermissionsContainer>

      {/* Drawer for create/edit */}
      <RepairOrderDrawer
        isOpen={drawerOpen}
        onClose={handleDrawerClose}
        orderRecno={selectedOrderRecno}
        onSaved={handleOrderSaved}
        viewers={selectedOrderRecno ? repairOrderViewers[selectedOrderRecno.toString()] || [] : []}
      />

      {/* Order details drawer */}
      <ProDetailSlideOver
        pronumber={selectedPro}
        isOpen={isOrderDrawerOpen && selectedPro !== null}
        onClose={closeOrderDrawer}
      />

      {/* Equipment ROs sidebar — all repair orders for a chassis/truck unit */}
      <EquipmentRepairOrdersDrawer
        unit={equipmentRosUnit}
        onClose={() => setEquipmentRosUnit(null)}
      />
    </div>
  );
};

const RepairOrders: React.FC = () => (
  <ViewedProsProvider>
    <RepairOrdersContent />
  </ViewedProsProvider>
);

export default RepairOrders;
